import type {
  CreateOrganizationRequest,
  Organization,
  UpdateOrganizationRequest,
} from '@nucleus-commerce/api-client/platform';

import type { ProfileValues } from '../schemas/profile-schema';
import type { ProvisionOutput } from '../schemas/provision-schema';

/** Form → DTO. Blank optional fields are omitted so the backend applies its defaults. */
export function toCreateOrganizationRequest(values: ProvisionOutput): CreateOrganizationRequest {
  const store = {
    ...(values.store.name ? { name: values.store.name } : {}),
    ...(values.store.slug ? { slug: values.store.slug } : {}),
    defaultCurrency: values.store.defaultCurrency,
    countryCode: values.store.countryCode,
    timezone: values.store.timezone,
  };
  return {
    name: values.name,
    slug: values.slug,
    ...(values.billingEmail ? { billingEmail: values.billingEmail } : {}),
    owner: {
      name: values.owner.name,
      email: values.owner.email,
      ...(values.owner.sendInvite ? {} : { password: values.owner.password }),
    },
    store,
  };
}

export function profileFormFor(organization: Organization): ProfileValues {
  return {
    name: organization.name,
    billingEmail: organization.billingEmail ?? '',
    notes: organization.notes ?? '',
  };
}

/** Only the fields that changed; a cleared email or note is sent as null, which clears it. */
export function toUpdateOrganizationRequest(
  values: ProfileValues,
  organization: Organization,
): UpdateOrganizationRequest {
  const billingEmail = values.billingEmail === '' ? null : values.billingEmail;
  const notes = values.notes.trim() === '' ? null : values.notes;
  return {
    ...(values.name !== organization.name ? { name: values.name } : {}),
    ...(billingEmail !== organization.billingEmail ? { billingEmail } : {}),
    ...(notes !== organization.notes ? { notes } : {}),
  };
}

const BYTE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'] as const;

/** Bytes as a value and an Intl unit (1 kB = 1000 B), for `format.number(value, { unit })`. */
export function bytesParts(bytes: number): { value: number; unit: (typeof BYTE_UNITS)[number] } {
  let value = bytes;
  let index = 0;
  while (value >= 1000 && index < BYTE_UNITS.length - 1) {
    value /= 1000;
    index += 1;
  }
  return { value, unit: BYTE_UNITS[index] ?? 'terabyte' };
}
